import { Component, computed, inject, input, output } from '@angular/core';
import {
  CheckpointItemDto,
  RepairTaskItemDto,
  RepairTaskService
} from '@artemis-software/wr-api';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { DeleteDialogComponent } from '@components/dialog/delete-dialog/delete-dialog.component';
import { openRepairTaskCloseDialog } from '@components/dialog/repair-task-close-dialog/repair-task-close-dialog.component';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { tryWithLoading } from '@/utils/async-utils';

const FINISHABLE_STATUSES: RepairTaskItemDto.StatusEnum[] = [
  RepairTaskItemDto.StatusEnum.Todo,
  RepairTaskItemDto.StatusEnum.InProgress,
];

@Component({
  standalone: false,
  selector: 'wr-repair-task-item',
  templateUrl: './repair-task-item.component.html',
  styleUrls: ['./repair-task-item.component.scss'],
})
export class RepairTaskItemComponent {
  private readonly nbDialogService = inject(NbDialogService);
  private readonly nbToastrService = inject(NbToastrService);
  private readonly repairTaskService = inject(RepairTaskService);

  readonly repairTask = input.required<RepairTaskItemDto>();
  readonly showDeleteButton = input(true);
  readonly showConfirmButton = input(false);
  readonly showSendEmailButton = input(false);
  readonly showDescription = input(false);

  readonly repairTaskDeleted = output<void>();
  readonly repairTaskFinished = output<void>();

  readonly canEdit = computed(() => this.repairTask().permissions.canEdit);
  readonly canFinish = computed(() =>
    this.repairTask().permissions.canClose && FINISHABLE_STATUSES.includes(this.repairTask().status));

  loading$ = new BehaviorSubject<boolean>(false);

  async showDeleteDialog(event: Event) {
    event.stopPropagation();
    if (await firstValueFrom(this.nbDialogService.open(DeleteDialogComponent, {
      context: {
        title: 'Reparaturauftrag löschen',
        message: 'Bist du sicher, dass du diesen Reparaturauftrag löschen willst?',
      },
    }).onClose)) {
      const id = this.repairTask().id;
      await tryWithLoading(this.loading$, async () => firstValueFrom(this.repairTaskService.deleteById(id)));
      this.repairTaskDeleted.emit();
      this.nbToastrService.success('Reparaturauftrag wurde erfolgreich gelöscht', 'Gelöscht');
    }
  }

  async setStatusFinished(event: Event) {
    event.stopPropagation();
    const task = this.repairTask();
    const result = await openRepairTaskCloseDialog(this.nbDialogService, 'finish', task.title);
    if (!result) {
      return;
    }
    try {
      await tryWithLoading(this.loading$, async () => firstValueFrom(this.repairTaskService.finish(task.id, result)));
    } catch {
      return;
    }
    this.nbToastrService.success('Reparaturauftrag wurde erfolgreich abgeschlossen', 'Abgeschlossen');
    this.repairTaskFinished.emit();
  }

  getCheckpointNumber(checkpoint: CheckpointItemDto | undefined): string {
    if (!checkpoint) {
      return '';
    }
    return checkpoint.checkpointGroupNumber + '.' + checkpoint.number;
  }
}
